import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { usePropertyPanel } from '../PropertyPanelContext';

// 토글 버튼 컴포넌트
const ToggleButton = ({ label, checked, onChange, fullWidth = false }) => {
  return (
    <div className="property-row" style={{ marginBottom: '10px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        width: '100%'
      }}>
        <span style={{ minWidth: fullWidth ? '50%' : '30%' }}>{label}</span>
        <div 
          style={{ 
            position: 'relative', 
            width: '44px', 
            height: '22px', 
            backgroundColor: checked ? '#3b82f6' : '#ccc',
            borderRadius: '34px', 
            transition: '.3s',
            cursor: 'pointer'
          }}
          onClick={() => onChange(!checked)}
        >
          <div 
            style={{ 
              position: 'absolute',
              height: '18px',
              width: '18px',
              left: checked ? '24px' : '2px',
              bottom: '2px',
              backgroundColor: 'white',
              transition: '.3s',
              borderRadius: '50%'
            }}
          />
        </div>
      </div>
    </div>
  );
};

const UserProfileProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  
  // PropertyPanelContext에서 필요한 함수 가져오기
  const { devicePropertyTab } = usePropertyPanel();
  
  // 콘텐츠 속성 변경 핸들러
  const handleContentChange = (property, value) => {
    if (!selectedComponent) return;
    
    // content 객체 복사
    const existingContent = JSON.parse(JSON.stringify(selectedComponent.content || {}));
    
    // device 속성이 있는지 확인하고 디바이스별 처리
    if (devicePropertyTab === 'pc') {
      // PC 모드에서는 기본 content 업데이트
      existingContent[property] = value;
    } else {
      // 모바일 모드에서는 deviceProperty 업데이트
      if (!existingContent.deviceProperty) {
        existingContent.deviceProperty = {
          mobile: {}
        };
      }
      if (!existingContent.deviceProperty.mobile) {
        existingContent.deviceProperty.mobile = {};
      }
      existingContent.deviceProperty.mobile[property] = value;
    }
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: existingContent
      }
    }));
  };
  
  // varFields 속성 변경 핸들러
  const handleVarFieldsChange = (varName, field, value) => {
    if (!selectedComponent) return;
    
    // 기존 varFields 복사 또는 기본값 생성
    const existingVarFields = selectedComponent.varFields || {};
    
    // varFields 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        varFields: {
          ...existingVarFields,
          [varName]: {
            ...existingVarFields[varName] || { show: false, label: '', type: 'input', options: [], required: false },
            [field]: value
          }
        }
      }
    }));
  };
  
  // basicFields 속성 변경 핸들러 추가
  const handleBasicFieldsChange = (fieldName, value) => {
    if (!selectedComponent) return;
    
    // 기존 basicFields 복사 또는 기본값 생성
    const existingBasicFields = selectedComponent.basicFields || {
      userId: true,
      name: true,
      password: true,
      phone: true,
      email: true,
      birthday: true,
      createdAt: true,
      address: true,
      referralLink: true
    };
    
    // basicFields 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        basicFields: {
          ...existingBasicFields,
          [fieldName]: value
        }
      }
    }));
  };
  
  // 필드 설정 렌더링 - var01, var02, var03 같은 변수 필드에 대한 처리
  const renderVarField = (varName, title) => {
    return (
      <>
        <div className="property-group-subtitle">
          {title} ({varName})
        </div>
        
        <ToggleButton
          label="표시 여부"
          checked={selectedComponent.varFields?.[varName]?.show ?? false}
          onChange={(value) => handleVarFieldsChange(varName, 'show', value)}
        />
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>필드 이름</label>
          <input 
            type="text" 
            value={selectedComponent.varFields?.[varName]?.label || ''}
            onChange={(e) => handleVarFieldsChange(varName, 'label', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        
        <ToggleButton
          label="필수 입력"
          checked={selectedComponent.varFields?.[varName]?.required ?? false}
          onChange={(value) => handleVarFieldsChange(varName, 'required', value)}
        />
      </>
    );
  };
  
  return (
    <>
      {/* 마이페이지 설정 */}
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          마이페이지 설정
        </div>
        
        {/* 제목 설정 추가 */}
        <div className="property-row">
          <label>제목</label>
          <input 
            type="text" 
            value={devicePropertyTab === 'pc' 
                    ? (selectedComponent.content?.title || '회원 정보') 
                    : (selectedComponent.content?.deviceProperty?.mobile?.title 
                      || selectedComponent.content?.title 
                      || '회원 정보')}
            onChange={(e) => handleContentChange('title', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {/* 회원정보 필드 설정 */}
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          회원정보 필드 설정
        </div>
        
        <ToggleButton
          label="아이디 필드 표시"
          checked={selectedComponent.basicFields?.userId !== false}
          onChange={(value) => handleBasicFieldsChange('userId', value)}
          fullWidth={true}
        />
        
        <ToggleButton
          label="이름 필드 표시"
          checked={selectedComponent.basicFields?.name !== false}
          onChange={(value) => handleBasicFieldsChange('name', value)}
          fullWidth={true}
        />
        
        <ToggleButton
          label="비밀번호 필드 표시"
          checked={selectedComponent.basicFields?.password !== false}
          onChange={(value) => handleBasicFieldsChange('password', value)}
          fullWidth={true}
        />
        
        <ToggleButton
          label="전화번호 필드 표시"
          checked={selectedComponent.basicFields?.phone !== false}
          onChange={(value) => handleBasicFieldsChange('phone', value)}
          fullWidth={true}
        />
        
        <ToggleButton
          label="이메일 필드 표시"
          checked={selectedComponent.basicFields?.email !== false}
          onChange={(value) => handleBasicFieldsChange('email', value)}
          fullWidth={true}
        />
        
        <ToggleButton
          label="생년월일 필드 표시"
          checked={selectedComponent.basicFields?.birthday !== false}
          onChange={(value) => handleBasicFieldsChange('birthday', value)}
          fullWidth={true}
        />
        
        <ToggleButton
          label="가입일 필드 표시"
          checked={selectedComponent.basicFields?.createdAt !== false}
          onChange={(value) => handleBasicFieldsChange('createdAt', value)}
          fullWidth={true}
        />
        
        <ToggleButton
          label="주소 필드 표시"
          checked={selectedComponent.basicFields?.address !== false}
          onChange={(value) => handleBasicFieldsChange('address', value)}
          fullWidth={true}
        />
        
        <ToggleButton
          label="추천링크 표시"
          checked={selectedComponent.basicFields?.referralLink !== false}
          onChange={(value) => handleBasicFieldsChange('referralLink', value)}
          fullWidth={true}
        />
      </div>

      {/* 커스텀 필드 설정 */}
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          커스텀 필드 설정
        </div>
        {renderVarField('var01', '추가 필드 1')}
        {renderVarField('var02', '추가 필드 2')}
        {renderVarField('var03', '추가 필드 3')}
      </div>
    </>
  );
};

export default UserProfileProperties; 